/*
  THAANI — Hero-Slider
  Vanilla JS, kein Framework. Wechselt Slide, Hintergrundfarbe und (optional)
  die globale Signalfarbe --thaani-accent, an der auch der Header haengt.
*/

const THAANI_ACCENT_FALLBACK = '#4b2673';
const THAANI_CONTRAST_FALLBACK = '#f7f1e8';

function thaaniHexToRgb(value) {
  if (typeof value !== 'string') return null;

  let hex = value.trim().replace('#', '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return null;

  const int = parseInt(hex, 16);

  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

// HSL-Helligkeit wie Liquids color_extract: 'lightness', damit Snippet und
// Skript beim Logo-Filter dieselbe Schwelle benutzen.
function thaaniIsLight(value) {
  const rgb = thaaniHexToRgb(value);
  if (!rgb) return false;

  const [r, g, b] = rgb.split(',').map((n) => parseInt(n, 10) / 255);
  const lightness = (Math.max(r, g, b) + Math.min(r, g, b)) / 2;

  return lightness > 0.5;
}

class ThaaniHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.thaani-hero__slide'));
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('.thaani-hero__dot'));
    this.prevButton = this.querySelector('.thaani-hero__arrow--prev');
    this.nextButton = this.querySelector('.thaani-hero__arrow--next');
    this.autoplayButton = this.querySelector('.thaani-hero__autoplay');
    this.status = this.querySelector('.thaani-hero__status');

    this.syncHeader = this.dataset.syncHeader === 'true';
    this.fokusAktiv = this.dataset.fokus !== 'false';
    this.fokusSchwelle = (parseInt(this.dataset.fokusAb, 10) || 55) / 100;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayRequested = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 6) * 1000;
    this.autoplayRunning = false;
    this.timer = null;
    this.index = 0;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);

    this.bindEvents();
    this.bindFokus();

    // Gesamtanimation erst starten, wenn die Assets des ersten Slides
    // (Bilder + Explosions-Video) geladen sind — sonst explodiert die
    // Choreografie ins Leere. Maximal 3s warten, dann trotzdem los;
    // solange zeigt 'is-loading' nur die Signalfarbe.
    this.classList.add('is-loading');

    const begin = () => {
      this.classList.remove('is-loading');
      this.classList.add('is-ready');

      const start = () => {
        this.select(0, { announce: false, warmUp: false });
        this.warmUpVideos(1500);
        if (this.autoplayRequested && this.slides.length > 1 && !this.reducedMotion.matches) {
          this.startAutoplay();
        }
      };

      if (this.reducedMotion.matches) {
        start();
      } else {
        // Erst-Lade-Choreografie: nach 'is-ready' einen Frame im Ausgangszustand
        // rendern lassen, damit Einflug/Stagger auch beim ersten Aufbau spielen.
        // rAF feuert in Hintergrund-Tabs nicht — Timeout-Fallback verhindert,
        // dass der Hero dort leer haengen bleibt.
        let started = false;
        const run = () => {
          if (started) return;
          started = true;
          start();
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
        setTimeout(run, 400);
      }
    };

    this.waitForAssets(this.slides[0]).then(begin);
  }

  // Sammelt Ladeversprechen der kritischen Assets eines Slides.
  // Race gegen 3s-Timeout: langsames Netz darf den Hero nie blockieren.
  waitForAssets(slide) {
    const waits = [];

    slide.querySelectorAll('img').forEach((img) => {
      if (img.complete || typeof img.decode !== 'function') return;
      waits.push(img.decode().catch(() => {}));
    });

    const video = slide.querySelector('.thaani-hero__bg-video');
    if (video && !this.reducedMotion.matches && video.readyState < 3) {
      waits.push(
        new Promise((resolve) => {
          video.addEventListener('canplaythrough', resolve, { once: true });
          video.addEventListener('error', resolve, { once: true });
        })
      );
    }

    if (waits.length === 0) return Promise.resolve();

    return Promise.race([Promise.all(waits), new Promise((resolve) => setTimeout(resolve, 3000))]);
  }

  /*
    Fokus-Blende: Sobald das Explosions-Video seinen Endpunkt erreicht,
    tritt der Hintergrund weich gezeichnet zurueck. Gemessen wird am
    tatsaechlichen Abspielstand, nicht an einem Timer — ein stockendes
    Video wuerde sonst aus dem Takt laufen.
  */
  bindFokus() {
    if (!this.fokusAktiv || this.reducedMotion.matches) return;

    this.slides.forEach((slide) => {
      const video = slide.querySelector('.thaani-hero__bg-video');
      if (!video) return;

      video.addEventListener('timeupdate', () => {
        // WebM ohne Dauer im Header meldet Infinity — dann traegt das
        // gepufferte Ende die Laenge, sonst griffe der Fokus erst bei 'ended'.
        let dauer = video.duration;
        if (!Number.isFinite(dauer) && video.seekable && video.seekable.length) {
          dauer = video.seekable.end(video.seekable.length - 1);
        }
        if (!Number.isFinite(dauer) || dauer === 0) return;
        slide.classList.toggle('is-fokus', video.currentTime / dauer >= this.fokusSchwelle);
      });

      // Ist das Video durch, bleibt der Fokus stehen — auch ohne weiteres timeupdate.
      video.addEventListener('ended', () => slide.classList.add('is-fokus'));
    });
  }

  // Nur das Video des naechsten Slides vorpuffern, damit der Sortenwechsel
  // ohne Ladepause explodiert. Alle sieben auf einmal waren 13 bis 21 MB
  // beim ersten Aufruf; die weiteren folgen bei jedem Wechsel (siehe select).
  // Bei Datensparmodus oder reduzierter Bewegung wird gar nichts vorgeladen.
  warmUpVideos(delay) {
    if (this.slides.length < 2 || this.reducedMotion.matches) return;
    if (navigator.connection && navigator.connection.saveData) return;

    const total = this.slides.length;
    const next = (this.index + 1) % total;

    const warm = () => {
      if (next === this.index) return;
      const video = this.slides[next].querySelector('.thaani-hero__bg-video');
      if (video && video.preload !== 'auto') {
        video.preload = 'auto';
        video.load();
      }
    };

    if (delay) {
      window.setTimeout(warm, delay);
    } else {
      warm();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.select(this.index - 1);
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.select(this.index + 1);
      });
    }

    this.dots.forEach((dot, dotIndex) => {
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.select(dotIndex);
      });
    });

    if (this.autoplayButton) {
      this.autoplayButton.addEventListener('click', () => {
        if (this.autoplayRunning) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      });
    }

    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.resumeAutoplay());
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.stopAutoplay();
      this.select(this.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.stopAutoplay();
      this.select(this.index - 1);
    }
  }

  handlePointerDown(event) {
    this.pointerStartX = event.clientX;
  }

  handlePointerUp(event) {
    if (typeof this.pointerStartX !== 'number') return;

    const delta = event.clientX - this.pointerStartX;
    this.pointerStartX = undefined;

    if (Math.abs(delta) < 50) return;

    this.stopAutoplay();
    this.select(delta < 0 ? this.index + 1 : this.index - 1);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pauseAutoplay();
    } else {
      this.resumeAutoplay();
    }
  }

  select(rawIndex, options) {
    const total = this.slides.length;
    const index = ((rawIndex % total) + total) % total;
    const settings = options || {};

    this.index = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.inert = !isActive;

      // Explosions-Video des aktiven Slides von vorn abspielen,
      // inaktive pausieren. Bei reduzierter Bewegung laeuft kein Video —
      // das CSS blendet stattdessen das statische Stimmungsbild ein.
      const video = slide.querySelector('.thaani-hero__bg-video');
      if (!video) return;

      // Der Fokus gehoert ans Ende des Videos — beim Wechsel faengt es von vorn an.
      slide.classList.remove('is-fokus');

      if (isActive && !this.reducedMotion.matches) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // Metadaten noch nicht da — Video startet dann ohnehin bei 0.
        }
        const playing = video.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      } else {
        video.pause();
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });

    this.applyAccent(this.slides[index]);

    if (this.status && settings.announce !== false) {
      this.status.textContent = `${index + 1} / ${total}`;
    }

    // Der uebernaechste Slide darf jetzt puffern (beim Start uebernimmt das
    // der verzoegerte Aufruf in connectedCallback).
    if (settings.warmUp !== false) {
      this.warmUpVideos();
    }
  }

  applyAccent(slide) {
    const accent = slide.dataset.accent || THAANI_ACCENT_FALLBACK;
    const contrast = slide.dataset.contrast || THAANI_CONTRAST_FALLBACK;
    const accentRgb = thaaniHexToRgb(accent);
    const contrastRgb = thaaniHexToRgb(contrast);

    this.style.setProperty('--thaani-hero-accent', accent);
    this.style.setProperty('--thaani-hero-contrast', contrast);

    if (accentRgb) this.style.setProperty('--thaani-hero-accent-rgb', accentRgb);
    if (contrastRgb) this.style.setProperty('--thaani-hero-contrast-rgb', contrastRgb);

    // Heller Button auf dunkler Flaeche wird beim Hover weiss, dunkler dunkelt nach.
    this.style.setProperty(
      '--thaani-button-hover-mix',
      contrast.toLowerCase() === THAANI_CONTRAST_FALLBACK ? '#ffffff' : '#000000'
    );

    if (!this.syncHeader) return;

    const root = document.documentElement;
    root.style.setProperty('--thaani-accent', accent);
    root.style.setProperty('--thaani-accent-contrast', contrast);

    if (accentRgb) root.style.setProperty('--thaani-accent-rgb', accentRgb);
    if (contrastRgb) root.style.setProperty('--thaani-accent-contrast-rgb', contrastRgb);

    // Ohne Maskensupport faerbt ein Filter die Wortmarke. Der muss beim
    // Slidewechsel mitwandern, sonst steht auf heller Signalfarbe ein helles
    // Logo. Schwelle wie in snippets/thaani-accent.liquid.
    root.style.setProperty(
      '--thaani-logo-filter',
      thaaniIsLight(contrast) ? 'brightness(0) invert(1)' : 'brightness(0)'
    );
  }

  startAutoplay() {
    if (this.slides.length < 2 || this.reducedMotion.matches) return;

    this.autoplayRunning = true;
    this.updateAutoplayButton();
    this.scheduleNext();
  }

  scheduleNext() {
    window.clearTimeout(this.timer);

    if (!this.autoplayRunning) return;

    this.timer = window.setTimeout(() => {
      this.select(this.index + 1, { announce: false });
      this.scheduleNext();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    window.clearTimeout(this.timer);
  }

  resumeAutoplay() {
    if (this.autoplayRunning) this.scheduleNext();
  }

  stopAutoplay() {
    this.autoplayRunning = false;
    window.clearTimeout(this.timer);
    this.updateAutoplayButton();
  }

  updateAutoplayButton() {
    if (!this.autoplayButton) return;

    const label = this.autoplayRunning
      ? this.autoplayButton.dataset.labelPause
      : this.autoplayButton.dataset.labelPlay;

    this.autoplayButton.setAttribute('aria-label', label || '');
    this.autoplayButton.setAttribute('aria-pressed', this.autoplayRunning ? 'false' : 'true');
    this.autoplayButton.textContent = this.autoplayRunning ? '❚❚' : '▶';
  }
}

if (!customElements.get('thaani-hero')) {
  customElements.define('thaani-hero', ThaaniHero);
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const hero = event.target.closest('thaani-hero');
    if (!hero || typeof hero.select !== 'function') return;

    hero.stopAutoplay();
    hero.select(parseInt(event.target.dataset.index, 10) || 0);
  });
}
